import type { ChatSession, ContractReview, Document, Message, SharedAnswer, User } from '@/types';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: unknown;
  headers?: Record<string, string>;
}

class ApiClient {
  private async request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const { method = 'GET', body, headers = {} } = options;

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...headers,
      },
      body: body ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'An error occurred' }));
      throw new Error(error.message || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  // Chat endpoints
  async createChat(): Promise<ChatSession> {
    return this.authenticatedRequest<ChatSession>('/v1/chat', { method: 'POST' });
  }

  async getChats(): Promise<ChatSession[]> {
    return this.authenticatedRequest<ChatSession[]>('/v1/chat');
  }

  async getChat(id: string): Promise<ChatSession> {
    return this.authenticatedRequest<ChatSession>(`/v1/chat/${id}`);
  }

  async sendMessage(chatId: string, content: string, effort: string): Promise<Message> {
    return this.authenticatedRequest<Message>(`/v1/chat/${chatId}/messages`, {
      method: 'POST',
      body: { content, effortLevel: effort },
    });
  }

  // Send message and get streaming response as plain text
  async sendMessageStream(chatId: string, content: string, effort: string): Promise<string> {
    const streamHeaders: Record<string, string> = { 'Content-Type': 'application/json' };
    if (this.clerkToken) {
      streamHeaders['Authorization'] = `Bearer ${this.clerkToken}`;
    }
    const response = await fetch(`${API_BASE_URL}/v1/chat/${chatId}/messages/stream`, {
      method: 'POST',
      headers: streamHeaders,
      body: JSON.stringify({ content, effortLevel: effort }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to stream message' }));
      throw new Error(error.message || `HTTP error! status: ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('No response body');
    }

    const decoder = new TextDecoder();
    let fullResponse = '';
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.slice(6);
          if (data === '[DONE]') continue;
          try {
            const parsed = JSON.parse(data);
            if (parsed.token) {
              fullResponse += parsed.token;
            }
          } catch {
            // Try parsing as raw text
            if (line.length > 5) {
              fullResponse += line;
            }
          }
        } else if (line.trim().length > 0) {
          // Raw text chunk
          fullResponse += line;
        }
      }
    }

    return fullResponse;
  }

  // Document endpoints
  async uploadDocument(file: File): Promise<Document> {
    const formData = new FormData();
    formData.append('file', file);

    const uploadHeaders: Record<string, string> = {};
    if (this.clerkToken) {
      uploadHeaders['Authorization'] = `Bearer ${this.clerkToken}`;
    }

    const response = await fetch(`${API_BASE_URL}/v1/files/upload`, {
      method: 'POST',
      headers: uploadHeaders,
      body: formData,
    });

    if (!response.ok) {
      throw new Error('Failed to upload document');
    }

    return response.json();
  }

  async getDocuments(): Promise<Document[]> {
    return this.authenticatedRequest<Document[]>('/v1/files');
  }

  async deleteDocument(id: string): Promise<void> {
    return this.authenticatedRequest<void>(`/v1/files/${id}`, { method: 'DELETE' });
  }

  // User endpoints
  async getUser(): Promise<User> {
    return this.authenticatedRequest<User>('/v1/auth/me');
  }

  async getTokenBalance(): Promise<{ balance: number }> {
    return this.authenticatedRequest<{ balance: number }>('/v1/auth/me');
  }

  // Subscription endpoints
  async getSubscription(): Promise<{
    tier: string;
    status: string;
    tokensRemaining: number;
    tokensPerMonth: number;
  }> {
    return this.authenticatedRequest('/v1/billing/info');
  }

  async purchaseTokens(packId: string): Promise<{ success: boolean; newBalance: number }> {
    return this.authenticatedRequest('/v1/billing/purchase', {
      method: 'POST',
      body: { packId },
    });
  }

  async redeemGiftCard(code: string): Promise<{ success: boolean; tokensAdded: number }> {
    return this.authenticatedRequest('/v1/billing/gift-cards/redeem', {
      method: 'POST',
      body: { code },
    });
  }

  // Get auth token from Clerk (call this before authenticated requests)
  private clerkToken: string | null = null;

  setAuthToken(token: string | null) {
    this.clerkToken = token;
  }

  private async authenticatedRequest<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const headers: Record<string, string> = { ...options.headers };
    if (this.clerkToken) {
      headers['Authorization'] = `Bearer ${this.clerkToken}`;
    }
    return this.request<T>(endpoint, { ...options, headers });
  }

  // Generated document endpoints
  async getDocumentTypes(): Promise<{ types: import('@/types').DocumentTypeInfo[] }> {
    return this.request('/v1/documents/types');
  }

  async generateDocument(chatId: string, documentType: import('@/types').DocumentType): Promise<{ document: import('@/types').GeneratedDocument }> {
    return this.authenticatedRequest('/v1/documents/generate', {
      method: 'POST',
      body: { chatId, documentType },
    });
  }

  async getGeneratedDocuments(): Promise<{ data: import('@/types').GeneratedDocument[]; pagination: { total: number; page: number; pageSize: number; totalPages: number } }> {
    return this.authenticatedRequest('/v1/documents');
  }

  async getGeneratedDocument(id: string): Promise<{ data: { document: import('@/types').GeneratedDocument } }> {
    return this.authenticatedRequest(`/v1/documents/${id}`);
  }

  async updateGeneratedDocument(id: string, content: import('@/types').DocumentContent): Promise<{ data: { document: import('@/types').GeneratedDocument } }> {
    return this.authenticatedRequest(`/v1/documents/${id}`, {
      method: 'PATCH',
      body: { content },
    });
  }

  async markDocumentDownloaded(id: string): Promise<void> {
    await this.authenticatedRequest(`/v1/documents/${id}/downloaded`, { method: 'POST' });
  }

  async deleteGeneratedDocument(id: string): Promise<void> {
    await this.authenticatedRequest(`/v1/documents/${id}`, { method: 'DELETE' });
  }

  // Feature 1: Update chat jurisdiction
  async updateChatJurisdiction(chatId: string, jurisdiction: string | null): Promise<void> {
    await this.authenticatedRequest(`/v1/chat/${chatId}`, {
      method: 'PATCH',
      body: { jurisdiction },
    });
  }

  // Feature 4: Contract review
  async reviewContract(filename: string, content: string, jurisdiction?: string | null): Promise<ContractReview> {
    const data = await this.authenticatedRequest<{ review: ContractReview }>('/v1/review', {
      method: 'POST',
      body: { filename, content, jurisdiction },
    });
    return data.review;
  }

  async getReviews(): Promise<ContractReview[]> {
    const data = await this.authenticatedRequest<{ items?: ContractReview[] }>('/v1/review');
    return data.items || [];
  }

  // Feature 5: Track referral clicks
  async trackReferralClick(chatId?: string): Promise<void> {
    await this.authenticatedRequest('/v1/referrals/click', {
      method: 'POST',
      body: { chatId, referralType: 'avvo' },
    }).catch(() => {}); // fire-and-forget
  }

  // Feature 8: Answer sharing
  async createShare(data: { chatId: string | null; question: string; answer: string; jurisdiction?: string | null }): Promise<{ shareId: string; url: string }> {
    const result = await this.authenticatedRequest<{ shareId: string; url: string }>('/v1/share', {
      method: 'POST',
      body: data,
    });
    return result;
  }

  async getSharedAnswer(shareId: string): Promise<SharedAnswer> {
    const response = await fetch(`${API_BASE_URL}/v1/share/${shareId}`);
    if (!response.ok) throw new Error('Share not found');
    const json = await response.json();
    return json.data as SharedAnswer;
  }
}

export const api = new ApiClient();
